import { SectionTitle } from "../components/SectionTitle";
import { TablePlot } from "../components/TablePlots";
import { CardContainer, CardLayout } from "../components/CardLayout";
import type { HomeData } from "../TrialHomeScreen";
import DonutPlot from "@/components/CategoricalPlots/barplot";
import SiteOverviewIcon from "@/assets/icons/sites.svg?react";
import { formatNumberForDisplay } from "@/utils/numberFormatting";
import { getAggregateMetricValue, getIpAdministrationData } from "../utils";

export type SitesOverviewCardData = Pick<HomeData, "sites">;

type SecondRowProps = {
  data: HomeData;
};

type FirstRowProps = {
  data: SitesOverviewCardData;
  enrollmentStats: Record<string, number>;
  screeningStats: Record<string, number>;
};

export const EnrollmentSection = ({ data }: { data: HomeData }) => {
  const enrollmentStats = {
    Complete: getAggregateMetricValue(
      data.enrollment_stats_complete ?? data.enrollment_stats?.Complete,
    ),
    Active: getAggregateMetricValue(
      data.enrollment_stats_active ?? data.enrollment_stats?.Active,
    ),
    "Early Termination": getAggregateMetricValue(
      data.enrollment_stats_early_termination ??
        data.enrollment_stats?.["Early Termination"],
    ),
  };
  const screeningStats = {
    Success: getAggregateMetricValue(
      data.screening_stats_success ?? data.screening_stats?.Success,
    ),
    "In Progress": getAggregateMetricValue(
      data.screening_stats_in_progress ?? data.screening_stats?.["In Progress"],
    ),
    Failure: getAggregateMetricValue(
      data.screening_stats_failure ?? data.screening_stats?.Failure,
    ),
  };

  return (
    <div data-testid="trial-home-enrollment-section">
      <SectionTitle title="ENROLLMENT" />
      <div className="grid lg:grid-cols-9 sm:grid-cols-2 grid-cols-1 gap-4">
        <div className="col-span-9">
          <FirstRow
            data={data}
            enrollmentStats={enrollmentStats}
            screeningStats={screeningStats}
          />
        </div>
        <div className="col-span-9">
          <SecondRow data={data} />
        </div>
      </div>
    </div>
  );
};

const SecondRow = ({ data }: SecondRowProps) => {
  const ipAdministrationData = getIpAdministrationData(data);

  return (
    <div className="grid lg:grid-cols-10 sm:grid-cols-4 grid-cols-1 gap-4 h-full">
      <div className="lg:col-span-2 sm:col-span-4 col-span-1">
        <div className="grid lg:grid-cols-1 sm:grid-cols-2 grid-cols-1 gap-4">
          <CardLayout
            title="Sex"
            subtitle="All subjects at all sites"
            fullHeight={true}
          >
            <div className="p-4">
              <TablePlot
                data={[data.enrollment_data.sex]}
                mode="distribution"
              />
            </div>
          </CardLayout>
          <CardLayout
            title="IP Administration Type"
            subtitle="Active subjects’ IP assignment"
            fullHeight={true}
          >
            <div className="p-4">
              <TablePlot data={[ipAdministrationData]} mode="distribution" />
            </div>
          </CardLayout>
        </div>
      </div>
      <div className="lg:col-span-3 sm:col-span-2 col-span-1">
        <CardLayout
          title="Race"
          subtitle="All subjects at all sites"
          fullHeight={true}
        >
          <div className="p-4 h-full">
            <TablePlot
              data={[data.enrollment_data.race]}
              mode="distribution"
              sorted={true}
            />
          </div>
        </CardLayout>
      </div>
      <div className="lg:col-span-3 sm:col-span-2 col-span-1 h-full">
        <CardLayout
          title="Age Group"
          fullHeight={true}
          subtitle="All subjects at all sites"
        >
          <div className="p-4 h-full">
            <TablePlot
              data={[
                data.enrollment_data.age || [],
                data.enrollment_data.age_grouped || [],
              ]}
              mode="distribution"
            />
          </div>
        </CardLayout>
      </div>
      {/* Age is derived from participant birth dates; when the source carries
          none, every statistic collapses to 0.0 and reads as a real measurement
          of a very young cohort. Leave the card out instead. */}
      {(data.current_age_stats?.["Sample Size (n)"] ?? 0) > 0 && (
        <div className="sm:col-span-2 col-span-1 h-full">
          <CardLayout
            title="Current Age"
            fullHeight={true}
            subtitle="All subjects at all sites"
          >
            <div className="p-4 h-full">
              <table className="w-full h-full">
                <tbody>
                  {Object.entries(data.current_age_stats).map(
                    ([key, value]: [string, number]) => (
                      <tr key={key}>
                        <td className="text-[14px] text-[#2B4752]">{key}</td>
                        <td className="text-[14px] text-end font-semibold text-[#2B4752]">
                          {key === "Sample Size (n)" ? value : value.toFixed(1)}
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          </CardLayout>
        </div>
      )}
    </div>
  );
};

const FirstRow = ({ data, enrollmentStats, screeningStats }: FirstRowProps) => {
  return (
    <div className="grid lg:grid-cols-10 sm:grid-cols-3 grid-cols-1 gap-4 h-full">
      <div className="lg:col-span-4 sm:col-span-3 col-span-1 h-full">
        <SitesOverviewCard data={data} />
      </div>
      <div className="lg:col-span-3 sm:col-span-3 col-span-1 h-full max-h-[250px]">
        <CardContainer className="h-full max-h-[250px]">
          <DonutPlot
            fontSize={10}
            className="missing-data sites-info"
            text={["Enrollment"]}
            keepDataOrder={true}
            data={enrollmentStats}
            subtext={["All subjects at all sites"]}
          />
        </CardContainer>
      </div>
      <div className="lg:col-span-3 sm:col-span-3 col-span-1 h-full">
        <CardContainer className="h-full max-h-[250px]">
          <DonutPlot
            fontSize={10}
            className="missing-data sites-info"
            text={["Screening"]}
            subtext={["All subjects at all sites"]}
            keepDataOrder={true}
            data={screeningStats}
          />
        </CardContainer>
      </div>
    </div>
  );
};

export const SitesOverviewCard = ({
  data,
}: {
  data: SitesOverviewCardData;
}) => {
  return (
    <CardContainer className="h-full">
      <div className="flex flex-row justify-between p-6 h-full">
        <div className="flex flex-col justify-between">
          <div className="flex flex-col items-start gap-[14px]">
            <div
              className="w-8 h-8 flex items-center justify-center rounded-lg"
              style={{ backgroundColor: "#D2EFF9" }}
            >
              <SiteOverviewIcon />
            </div>
            <h1 className="text-[18px] font-semibold">Site Overview</h1>
          </div>
          <div>
            <p
              className="text-[36px] font-semibold"
              data-testid="sites-overview-total-activated-sites"
            >
              {data.sites.total_sites ?? data.sites.total ?? 0}
            </p>
            <p className="text-[14px]">Total Activated Sites</p>
          </div>
        </div>
        <div className="flex flex-col items-end justify-between gap-2">
          <div className="flex flex-col items-end">
            <p className="text-[14px] text-[#8C8C8C]">COUNTRIES</p>
            <p className="text-[18px]">{data.sites.countries}</p>
          </div>
          <div className="flex flex-col items-end">
            <p className="text-[14px] text-[#8C8C8C]">REGIONS</p>
            <p className="text-[18px]">{data.sites.regions}</p>
          </div>
          <div className="flex flex-col items-end">
            <p className="text-[14px] text-[#8C8C8C]">AVERAGE MONTHS OPEN</p>
            <p className="text-[18px]">
              {formatNumberForDisplay(data.sites.open_stats.average)}±
              {formatNumberForDisplay(data.sites.open_stats.std)}
            </p>
            <p className="text-[14px] italic">
              Range: {formatNumberForDisplay(data.sites.open_stats.range_low)}–
              {formatNumberForDisplay(data.sites.open_stats.range_high)}
            </p>
          </div>
        </div>
      </div>
    </CardContainer>
  );
};
